import { css } from '@emotion/css';
import styled from '@emotion/styled';
import Button from '@shared/components/core/button/CoreButton';
import TextButton from '@shared/components/core/button/TextButton';
import Icons from '@shared/components/core/images/Icons';
import ActionCardWrapper from '@shared/components/core/layouts/ActionCardWrapper';
import ChipButtonContainer from '@shared/components/core/layouts/ChipButtonContainer';
import MenuCardContainer from '@shared/components/core/layouts/MenuCardContainer';
import { CardTitle } from '@shared/components/core/text/CardTypography';
import { BodySmall } from '@shared/components/core/text/Typography';
import { ControlTypeField } from '@shared/components/input/field/ControlTypeField';
import MacroStepEditor from '@shared/components/macro/field/MacroStepEditor';
import { CommandType, allControls, commandOptions, commandTypesDisplay } from '@shared/constants/CommandValues';
import { createUUID } from '@shared/constants/Keys';
import { getCommandDisplayName } from '@shared/hooks/helpers/profileUtils';
import AlertsContainer from '@shared/hooks/state/AlertsContainer';
import EditProfileContainer from '@shared/hooks/state/EditProfileContainer';
import { MacroEventType } from '@shared/models/deviceProfiles/MacroEvent';
import { MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

export default function ProfileControlBuilderPage() {
    const { index } = useParams();
    const { t } = useTranslation();
    const editProfileContainer = EditProfileContainer.useContainer();
    const alertsContainer = AlertsContainer.useContainer();
    const navigate = useNavigate();
    const isNew = index?.toLowerCase() == 'new';
    const [macro, setMacro] = useState<MacroDisplayModel>(
        isNew
            ? ({ commands: [], events: [], id: createUUID() } as MacroDisplayModel)
            : editProfileContainer.macros[Number(index)]
    );
    const [stage, setStage] = useState<number>(0);

    const handleFinish = async () => {
        if (macro.commands.filter((c) => c.command?.length > 0)?.length < 1) {
            alertsContainer.showError(t('common:event.error'));
            return;
        }
        if (macro.events.length < 1) {
            alertsContainer.showError(t('common:event.error'));
        }
        if (macro.commands.some((c) => c.commandType == 'hotkey')) {
            const originalValue = macro.commands?.[0]?.command;
            const hotkeyCommand = macro.commands?.[0]?.command;
            const hotkeyId = editProfileContainer.updateHotkeyFromMacro(hotkeyCommand, macro, 0, originalValue);
            macro.commands[0].hotkeyLocalId = hotkeyId ?? undefined;
        }
        const macros = editProfileContainer.macrosRef.current;
        if (isNew) {
            macros.unshift({
                ...macro,
                name: getCommandDisplayName(macro.commands?.[0]?.command, t),
            });
        } else {
            macros[Number(index)] = macro;
        }

        // update macros in profile
        editProfileContainer.updateMacros(macros);
        editProfileContainer.setHasChanges(true);
        navigate(-1);
    };

    const handleAddStep = (eventType?: MacroEventType) => {
        setStage(3);
        macro.events.push({
            eventType: eventType ?? 'KeyPress',
            keys: [''],
            holdTimeMilliseconds: undefined,
        });
        setMacro({ ...macro });
    };
    const handleCommandChange = (value: string, index: number) => {
        macro.commands[index].command = value;
        setMacro({ ...macro });
    };

    const selectCommandType = (commandType: CommandType) => {
        if (macro.commands.length < 2) {
            macro.commands = [
                {
                    commandType,
                    command: '',
                },
            ];
        } else {
            macro.commands.push({
                commandType,
                command: '',
            });
        }

        setStage(1);
    };

    const goBack = () => {
        setStage(stage - 1);
    };

    return (
        <>
            <MenuCardContainer>
                {stage == 0 && (
                    <StyledActionCardWrapper>
                        <CardTitle>{t('common:input.select')} </CardTitle>
                        <BodySmall>{t('common:input.question')}</BodySmall>
                        {commandOptions.map((c, i) => (
                            <Button
                                id={c}
                                $color="secondary"
                                key={`commandType-${i}`}
                                startIcon={commandTypesDisplay[c].icon}
                                onClick={() => selectCommandType(c)}>
                                {t(commandTypesDisplay[c].title)}
                            </Button>
                        ))}
                    </StyledActionCardWrapper>
                )}

                {stage == 1 && (
                    <>
                        <StyledActionCardWrapper>
                            <>
                                <CardTitle>
                                    <Icons name={commandTypesDisplay[macro.commands?.[0]?.commandType].icon} />
                                    {t(commandTypesDisplay[macro.commands?.[0]?.commandType].title)}
                                </CardTitle>
                                <BodySmall>
                                    {t(commandTypesDisplay[macro.commands?.[0]?.commandType].question)}
                                </BodySmall>
                                <ControlTypeField
                                    id="input-command-field"
                                    control={macro.commands?.[0]?.commandType}
                                    value={macro.commands?.[0]?.command}
                                    onChange={(e) => handleCommandChange(e, 0)}
                                />
                                {allControls.find((e) => e.value == macro.commands?.[0]?.command) && (
                                    <BodySmall>
                                        {t(
                                            allControls.find((e) => e.value == macro.commands?.[0]?.command)
                                                ?.description
                                        )}
                                    </BodySmall>
                                )}
                            </>
                            <Button
                                id="macro-edit-next"
                                onClick={() => {
                                    if (macro.commands?.[0]?.command?.length > 0) setStage(2);
                                }}>
                                {t('common:next')}
                            </Button>
                            <Button $color="simple" onClick={goBack}>
                                {t('common:back')}
                            </Button>
                        </StyledActionCardWrapper>
                    </>
                )}

                {stage == 2 && (
                    <StyledActionCardWrapper>
                        <CardTitle>{t('common:event.select')}</CardTitle>
                        <BodySmall>{t('common:event.question')}</BodySmall>
                        <Button $color="secondary" onClick={() => handleAddStep('KeyPress')}>
                            {t('common:event.keyPress.title')}
                        </Button>
                        <Button $color="secondary" onClick={() => handleAddStep('KeyRelease')}>
                            {t('common:event.keyRelease.title')}
                        </Button>
                        <Button $color="secondary" onClick={() => handleAddStep('KeyToggle')}>
                            {t('common:event.keyToggle.title')}
                        </Button>
                        <Button id="event-type" $color="secondary" onClick={() => handleAddStep('Type')}>
                            {t('common:event.type.title')}
                        </Button>
                        <Button $color="secondary" onClick={() => handleAddStep('MouseMove')}>
                            {t('common:event.mouseMove.title')}
                        </Button>
                        <Button $color="secondary" onClick={() => handleAddStep('JoysticksMove')}>
                            {t('common:event.joystickMove.title')}
                        </Button>
                        <Button $color="secondary" onClick={() => handleAddStep('PlayAudio')}>
                            {t('common:event.playAudio.title')}
                        </Button>
                        <Button $color="secondary" onClick={() => handleAddStep('StopOutputs')}>
                            {t('common:event.stopOutputs.title')}
                        </Button>
                        <Button $color="secondary" onClick={() => handleAddStep('DeviceTypeCustomAction')}>
                            {t('common:event.deviceTypeCustomAction.title')}
                        </Button>
                        <Button
                            $color="secondary"
                            onClick={() => {
                                handleAddStep();
                                handleAddStep();
                            }}>
                            {t('common:event.multiple')}
                        </Button>
                        <Button $color="simple" onClick={goBack}>
                            {t('common:back')}
                        </Button>
                    </StyledActionCardWrapper>
                )}

                {stage == 3 && (
                    <>
                        <StyledActionCardWrapper>
                            <CardTitle>{t('common:event.setup')}</CardTitle>
                            <BodySmall>{t('common:event.question')}</BodySmall>

                            <Button onClick={() => handleFinish()}>{t('common:finish')}</Button>
                            <Button $color="simple" onClick={goBack}>
                                {t('common:back')}
                            </Button>
                        </StyledActionCardWrapper>
                        {macro.events?.map((e, ei) => (
                            <MacroStepEditor
                                key={ei}
                                macro={macro}
                                step={e}
                                stepIndex={ei}
                                onChange={(macro) => setMacro(macro)}
                            />
                        ))}

                        <ChipButtonContainer
                            className={css`
                                margin: 16px;
                                width: calc(100% - 32px);
                            `}>
                            <TextButton
                                startIcon="plus"
                                disabled={editProfileContainer.isPreview}
                                onClick={() => handleAddStep()}>
                                {t('common:event.add')}
                            </TextButton>
                        </ChipButtonContainer>

                        <ChipButtonContainer
                            className={css`
                                margin: 16px;
                                width: calc(100% - 32px);
                            `}>
                            <Button id="finish-macro-edit" onClick={() => handleFinish()}>
                                {t('common:finish')}
                            </Button>
                        </ChipButtonContainer>
                    </>
                )}
            </MenuCardContainer>
        </>
    );
}

const StyledActionCardWrapper = styled(ActionCardWrapper)`
    margin: 16px;
    width: calc(100% - 32px);
`;
